'use client'

import { useEffect, useMemo, useState } from 'react'
import { CheckCheck, Search, X } from 'lucide-react'
import { createPortal } from 'react-dom'

import type { RouterOutputs } from '@package/api'
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from '@ui/web/components'
import { getInitials } from '@helper/utils/format'
import cn from '@config/tailwind/cn'

interface RecipientDialogProps {
  data: RouterOutputs['myBookCollection']['getAllSentBookCollections']['data'][number]['sentBookCollectionToUsers']
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function RecipientDialog({ data, open, onOpenChange }: RecipientDialogProps) {
  const [searchTerm, setSearchTerm] = useState('')

  const filteredData = useMemo(() => {
    if (!searchTerm.trim()) return data
    const lower = searchTerm.toLowerCase()
    return data.filter((item) => item.user.name.toLowerCase().includes(lower))
  }, [data, searchTerm])

  useEffect(() => {
    if (!open) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onOpenChange(false)
    }
    document.addEventListener('keydown', onKeyDown)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.body.style.overflow = ''
    }
  }, [open, onOpenChange])

  if (!open) return null

  const content = (
    <div
      className='srd:fixed srd:inset-0 srd:z-100 srd:flex srd:items-center srd:justify-center srd:p-4'
      role='dialog'
      aria-modal='true'
      aria-labelledby='recipient-dialog-title'
    >
      <div
        className='srd:fixed srd:inset-0 srd:bg-neutrals-black/30 srd:backdrop-blur-sm'
        aria-hidden
        onClick={() => onOpenChange(false)}
      />
      <div
        className='srd:relative srd:z-10 srd:flex srd:max-h-[95dvh] srd:w-full srd:max-w-md srd:flex-col srd:gap-y-4 srd:overflow-y-auto srd:rounded-lg srd:bg-neutrals-white srd:p-6 srd:shadow-xl srd:ring-1 srd:ring-neutrals-n-20'
        onClick={(e) => e.stopPropagation()}
      >
        <div className='srd:flex srd:items-start srd:justify-between srd:gap-2'>
          <div className='srd:flex srd:flex-col srd:gap-2'>
            <h2
              id='recipient-dialog-title'
              className='srd:text-base srd:font-medium srd:leading-none srd:text-complementary-black'
            >
              Penerima
            </h2>
            <p className='srd:text-sm srd:text-neutrals-n-75'>Nama-nama penerima kiriman kamu.</p>
          </div>
          <Button
            type='button'
            variant='ghost'
            size='icon'
            className='srd:shrink-0'
            onClick={() => onOpenChange(false)}
            aria-label='Tutup'
          >
            <X className='srd:size-5 srd:text-main-blue-100' />
          </Button>
        </div>

        <header className='srd:flex srd:flex-col srd:gap-y-1'>
          <p>Menampilkan {filteredData.length} nama</p>
          <InputGroup>
            <InputGroupAddon align='inline-start'>
              <Search className='srd:size-4 srd:text-main-blue-100' />
            </InputGroupAddon>
            <InputGroupInput
              placeholder='Cari nama penerima...'
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </InputGroup>
        </header>

        <div className='srd:flex srd:max-h-[80vh] srd:w-full srd:flex-col srd:items-start srd:gap-y-2 srd:overflow-y-auto'>
          {filteredData.map((item) => (
            <div key={item.id} className='srd:flex srd:w-full srd:items-center srd:justify-between srd:gap-x-4'>
              <div className='srd:flex srd:items-center srd:gap-x-2'>
                <Avatar profile='user' className='srd:size-10'>
                  <AvatarImage src={item.user.image ?? undefined} alt={item.user.name} />
                  <AvatarFallback>{getInitials(item.user.name)}</AvatarFallback>
                </Avatar>
                <p className='srd:max-w-full srd:truncate srd:w-fit'>{item.user.name}</p>
              </div>
              <CheckCheck
                className={cn(
                  'srd:size-4 srd:text-complementary-purple-100',
                  item.readAt ? 'srd:opacity-100' : 'srd:opacity-50',
                )}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  )

  return createPortal(content, document.body)
}
